<template>
  <form @submit.prevent="terminarVenta">
    <div class="modal-card dialogo-venta">
      <header class="modal-card-head dialogo-head success-head">
        <div class="head-content">
          <b-icon icon="cash-register" size="is-small" class="head-icon" />
          <p class="modal-card-title">Terminar venta</p>
        </div>
        <button type="button" class="delete" @click="$emit('close', 'venta')" />
      </header>

      <section class="modal-card-body">
        <!-- Cliente -->
        <busqueda-cliente
          @seleccionado="onSeleccionado"
          :initialCliente="cliente.nombre ? cliente : null"
        />

        <!-- Método de pago -->
        <b-field class="mt-3" label="Método de pago">
          <b-select
            class="wide"
            placeholder="Seleccionar..."
            icon="credit-card-outline"
            v-model="idMetodo"
            required
          >
            <option
              v-for="metodo in metodosSimples"
              :key="metodo"
              :value="metodo"
            >
              {{ metodo }}
            </option>
            <option
              v-for="metodo in metodos"
              :key="metodo.id"
              :value="metodo.id"
            >
              {{ metodo.nombre }}
            </option>
          </b-select>
        </b-field>

        <!-- Origen (solo para métodos no simples) -->
        <b-field label="Referencia / Origen" v-if="!esSimple && idMetodo">
          <b-input
            type="text"
            v-model="origen"
            placeholder="Nro. de referencia o descripción"
            icon="pound"
            required
          />
        </b-field>

        <!-- Moneda -->
        <b-field class="mt-3" label="Factura en">
          <b-select
            class="wide"
            icon="currency-usd"
            v-model="moneda"
            required
            @input="manejarMoneda"
          >
            <option value="usd">Dólares ($)</option>
            <option value="bs">Bolívares (Bs.)</option>
          </b-select>
        </b-field>

        <!-- Delivery toggle -->
        <div class="delivery-toggle mt-3">
          <b-switch
            v-model="esDelivery"
            @input="manejarEsDelivery"
            type="is-info"
          >
            Incluir servicio de delivery
          </b-switch>
        </div>

        <!-- Sección delivery -->
        <div v-if="esDelivery" class="delivery-section">
          <p class="seccion-titulo">
            <b-icon icon="truck-delivery" size="is-small" />
            Datos del delivery
          </p>

          <div class="columns is-gapless-v">
            <div class="column">
              <b-field label="Costo del delivery">
                <b-input
                  step="0.01"
                  icon="currency-usd"
                  type="number"
                  placeholder="0.00"
                  v-model="delivery.costo"
                  @input="manejarCostoDelivery"
                  required
                />
              </b-field>
            </div>
            <div
              class="column is-narrow"
              style="
                display: flex;
                align-items: flex-end;
                padding-left: 0.5rem;
                padding-bottom: 0.9rem;
              "
            >
              <b-switch
                v-model="delivery.gratis"
                type="is-success"
                @input="manejarDeliveryGratis"
              >
                Gratis para el cliente
              </b-switch>
            </div>
          </div>

          <b-field label="Destino">
            <b-input
              placeholder="Calle, casa, barrio, ciudad"
              v-model="delivery.destino"
              icon="map-marker"
              required
            />
          </b-field>

          <b-field label="Chofer(es)">
            <b-autocomplete
              v-model="busquedaChofer"
              :data="choferesFiltrados"
              field="nombre"
              placeholder="Buscar chofer..."
              open-on-focus
              @select="agregarChofer"
              :keep-first="true"
              icon="account-search"
              clearable
            >
              <template #empty>No se encontraron choferes</template>
            </b-autocomplete>
          </b-field>
          <div class="tags mt-1">
            <span
              v-for="(chofer, index) in choferesSeleccionados"
              :key="chofer.id"
              class="tag is-info is-medium"
            >
              {{ chofer.nombre }}
              <button
                class="delete is-small"
                type="button"
                @click="eliminarChofer(index)"
              />
            </span>
          </div>
        </div>

        <!-- Monto pagado -->
        <b-field class="mt-3" label="El cliente paga con">
          <b-input
            step="any"
            icon="currency-usd"
            type="number"
            placeholder="0.00"
            v-model="pagado"
            required
          />
        </b-field>

        <!-- Resumen de pago -->
        <div class="pago-resumen">
          <div class="pago-fila">
            <span>Total</span>
            <span class="pago-monto">${{ formatoMonto(totalVenta) }}</span>
          </div>
          <div class="pago-fila" v-if="pagado">
            <span>Pagado</span>
            <span class="pago-monto">${{ formatoMonto(pagado) }}</span>
          </div>
          <!-- BUG FIX: cambio = pagado - totalVenta (antes estaba invertido: totalVenta - pagado) -->
          <div class="pago-fila cambio-fila" v-if="pagado">
            <span>Cambio</span>
            <span
              class="pago-monto cambio-valor"
              :class="{ negativo: cambio < 0 }"
            >
              ${{ formatoMonto(Math.abs(cambio)) }}
              <span v-if="cambio < 0" class="text-danger"> (falta)</span>
            </span>
          </div>
        </div>
      </section>

      <footer class="modal-card-foot dialogo-foot">
        <b-button
          label="Cancelar"
          icon-left="cancel"
          size="is-small"
          @click="$emit('close', 'venta')"
        />
        <b-button
          label="Registrar venta"
          type="is-success"
          icon-left="check"
          size="is-small"
          native-type="submit"
        />
      </footer>
    </div>
  </form>
</template>

<script>
import BusquedaCliente from "../Clientes/BusquedaCliente";
import { TIPOS_PAGO_SIMPLE, TIPOS_CLIENTE } from "@/consts";
import Utiles from "../../Servicios/Utiles";

export default {
  name: "DialogoTerminarVenta",
  components: { BusquedaCliente },

  props: {
    totalVenta: { type: [String, Number], default: 0 },
    metodos: { type: Array, default: () => [] },
    choferes: { type: Array, default: () => [] },
    initialPagado: { type: [String, Number], default: "" },
    initialIdMetodo: { type: [String, Number], default: null },
    initialOrigen: { type: String, default: "" },
    initialCliente: { type: Object, default: () => ({}) },
    initialDelivery: {
      type: Object,
      default: () => ({
        costo: null,
        destino: null,
        gratis: false,
        idChofer: null,
      }),
    },
    initialChofer: {
      type: Object,
      default: () => ({ nombre: null, ci: null, tipo: null, telefono: null }),
    },
    initialChoferesSeleccionados: { type: Array, default: () => [] },
  },

  data() {
    return {
      moneda: "usd",
      pagado: this.initialPagado,
      idMetodo: this.initialIdMetodo,
      origen: this.initialOrigen,
      cliente: { ...this.initialCliente },
      esDelivery: !!(
        this.initialDelivery &&
        ((Array.isArray(this.initialDelivery.idChofer) &&
          this.initialDelivery.idChofer.length > 0) ||
          (typeof this.initialDelivery.idChofer === "string" &&
            this.initialDelivery.idChofer !== "") ||
          (typeof this.initialDelivery.idChofer === "number" &&
            this.initialDelivery.idChofer))
      ),
      nuevoChofer: false,
      delivery: {
        ...this.initialDelivery,
        idChofer: Array.isArray(this.initialDelivery?.idChofer)
          ? this.initialDelivery.idChofer
          : this.initialDelivery?.idChofer !== null &&
            this.initialDelivery?.idChofer !== undefined
          ? [this.initialDelivery.idChofer]
          : [],
        gratis: Boolean(this.initialDelivery?.gratis),
      },
      chofer: { ...this.initialChofer },
      metodosSimples: Object.values(TIPOS_PAGO_SIMPLE),
      tipos: TIPOS_CLIENTE,
      busquedaChofer: "",
      choferesSeleccionados: [...this.initialChoferesSeleccionados],
    };
  },

  computed: {
    esSimple() {
      return this.metodosSimples.includes(this.idMetodo);
    },
    // BUG FIX: cambio = pagado - totalVenta (correcto: lo que devuelve el cliente)
    // Antes estaba: totalVenta - pagado (invertido, devolvía negativo cuando pagado > total)
    cambio() {
      const pagado = parseFloat(this.pagado) || 0;
      const total = parseFloat(this.totalVenta) || 0;
      return pagado - total;
    },
    choferesFiltrados() {
      return this.choferes.filter(
        (c) =>
          !this.choferesSeleccionados.some((sc) => sc.id === c.id) &&
          c.nombre.toLowerCase().includes(this.busquedaChofer.toLowerCase())
      );
    },
  },

  methods: {
    formatoMonto(valor) {
      return Utiles.formatoMonto(valor);
    },
    onSeleccionado(cliente) {
      this.cliente = cliente;
      this.delivery.destino = cliente.direccion;
    },
    manejarEsDelivery() {
      this.$emit("actualizar", "esDelivery", this.esDelivery);
    },
    manejarCostoDelivery() {
      this.$emit("actualizar", "costoDelivery", this.delivery.costo);
    },
    manejarDeliveryGratis() {
      this.$emit("actualizar", "deliveryGratis", this.delivery.gratis);
    },
    manejarMoneda() {
      this.$emit("actualizar", "moneda", this.moneda);
    },
    terminarVenta() {
      if (
        this.pagado === "" ||
        parseFloat(this.pagado) < parseFloat(this.totalVenta)
      ) {
        this.$buefy.toast.open({
          type: "is-danger",
          message: "El monto pagado debe ser igual o mayor al total.",
        });
        return;
      }

      let payload = {
        tipo: "venta",
        pagado: this.pagado,
        cambio: this.cambio,
        cliente: this.cliente,
        moneda: this.moneda,
      };

      if (this.esDelivery) {
        if (this.choferesSeleccionados.length === 0) {
          this.$buefy.toast.open({
            type: "is-danger",
            message: "Debes seleccionar al menos un chofer.",
          });
          return;
        }
        payload.delivery = {
          ...this.delivery,
          idChofer: this.choferesSeleccionados.map((c) => c.id),
        };
        payload.chofer = this.chofer;
      }

      if (this.esSimple) {
        payload.simple = this.idMetodo;
      } else {
        payload.idMetodo = this.idMetodo;
        payload.origen = this.origen;
      }

      this.$emit("terminar", payload);
    },
    agregarChofer(chofer) {
      if (
        chofer &&
        !this.choferesSeleccionados.some((c) => c.id === chofer.id)
      ) {
        this.choferesSeleccionados.push(chofer);
        this.busquedaChofer = "";
      }
    },
    eliminarChofer(index) {
      this.choferesSeleccionados.splice(index, 1);
    },
  },
};
</script>

<style scoped>
.dialogo-venta {
  width: 520px;
  max-width: 95vw;
}
.dialogo-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.head-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.head-icon {
  opacity: 0.75;
}
.success-head {
  border-bottom: 2px solid var(--color-success) !important;
}
.seccion-titulo {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-text-muted);
  margin: 1rem 0 0.75rem;
}
.delivery-section {
  background: var(--color-surface2);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 0.85rem 1rem;
  margin-top: 0.5rem;
}
.delivery-toggle {
  margin-bottom: 0.25rem;
}
.pago-resumen {
  background: var(--color-surface2);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 0.75rem 1rem;
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.pago-fila {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 0.84rem;
  color: var(--color-text-muted);
}
.pago-monto {
  font-family: var(--font-mono);
  font-weight: 600;
  color: var(--color-text);
}
.cambio-fila {
  border-top: 1px solid var(--color-border);
  padding-top: 0.35rem;
  margin-top: 0.1rem;
}
.cambio-valor {
  color: var(--color-success) !important;
  font-size: 1rem;
}
.cambio-valor.negativo {
  color: var(--color-danger) !important;
}
.dialogo-foot {
  justify-content: flex-end;
  gap: 0.5rem;
}
.columns.is-gapless-v {
  margin-bottom: 0;
}
</style>
